import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { FcChoiceDialogService } from '@core/components/fc-choice-dialog/fc-choice-dialog.service';
import { CustomMessagesService } from '@core/errors/custom-messages.service';
import { CustomField, Equipment, EquipmentType, UploadCredentialEntityEnum } from '@core/interfaces';
import { UploadService } from '@core/services/upload.service';
import { getErrorCode } from '@core/utils/get-error-code';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { RecurrenceService } from 'app/main/recurrences/services/recurrence.service';
import { ImportsService } from 'app/main/settings/imports/services/imports.service';
import { EquipmentsTypesApi } from 'app/main/settings/registers/equipments-types/services/api/equipments-types-api.service';
import { isValid } from 'date-fns';
import { sentry } from 'sentry';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { EquipmentsService } from '../services/equipments-resolver.service';
import { TabQrCodeComponent } from './tabs/qrcode/tab-qr-code.component';

import { MatTabChangeEvent } from '@angular/material';
import english from '../i18n/en.json';
import spanish from '../i18n/es.json';
import portuguese from '../i18n/pt.json';
import { DialogEquipmentTabsEnum } from './dialog-equipments.types';

const QR_CODE_TAB_INDEX = 5;

export interface EquipmentDialogData {
  equipment: Equipment;
  selectedTab: DialogEquipmentTabsEnum;
  disableEditLocation: boolean;
}

export interface EquipmentDialogResult {
  reload?: boolean;
  equipment?: Equipment;
}

@Component({
  selector: 'dialog-equipments',
  templateUrl: './dialog-equipments.component.html',
  styleUrls: ['./dialog-equipments.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogEquipmentComponent implements OnInit, AfterViewInit {

  public equipment: Equipment;
  public equipmentId: string;
  public equipmentForm: FormGroup;
  public selectedTabIndex = 0;
  public isUpdate = false;
  public isSaving = false;
  public isViewMode = !this.authenticationService.hasPermission('equipments-edit');
  public hasFlagWarranties = this.authenticationService.hasFlags(['warranties']);
  public hasCreateWarrantyPermission = this.authenticationService.hasPermission('warranties-create');
  public hasEditWarrantyPermission = this.authenticationService.hasPermission('warranties-edit');
  public reloadDataTable = false;

  @ViewChild(TabQrCodeComponent, { static: false })
  public tabQrCodeComponent: TabQrCodeComponent;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private readonly data,
    private readonly matDialogRef: MatDialogRef<DialogEquipmentComponent, EquipmentDialogResult>,
    private readonly customMessageService: CustomMessagesService,
    private readonly authenticationService: AuthenticationService,
    private readonly equipmentsTypesApi: EquipmentsTypesApi,
    private readonly equipmentsService: EquipmentsService,
    private readonly recurrenceService: RecurrenceService,
    private readonly progressBar: FuseProgressBarService,
    private readonly importsService: ImportsService,
    private readonly uploadService: UploadService,
    private readonly formBuilder: FormBuilder,
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly fcChoiceDialogService: FcChoiceDialogService,
    private readonly translateService: TranslateService
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.equipment = this.data.equipment;
    this.isUpdate = this.data.equipment && this.data.equipment.id;
    this.equipmentId = this.isUpdate ? this.data.equipment.id : null;

    this.equipmentForm = this.formBuilder.group({
      name: new FormControl(this.equipment.name || null, [Validators.required]),
      number: new FormControl(this.equipment.number || null, [Validators.required]),
      qrCode: new FormControl(this.equipment.qrCode || null, [Validators.required]),
      notes: new FormControl(this.equipment.notes || ''),
      type: new FormControl(this.equipment.type || null, [Validators.required]),
      customFields: new FormArray([]),
    });

    this.equipmentForm.addControl('location', new FormControl({
      value: this.equipment.location || null,
      disabled: this.data.disableEditLocation
    }, [Validators.required]));
    this.equipmentForm.addControl('sector', new FormControl({
      value: this.equipment.locationSector || null,
      disabled: !this.equipment.location
    }));
    this.equipmentForm.addControl('environment', new FormControl({
      value: this.equipment.locationEnvironment || null,
      disabled: !this.equipment.locationSector
    }));
    this.equipmentForm.addControl('imageUrl', new FormControl(this.equipment.imageUrl ? { thumb: this.equipment.imageUrl } : null));
    this.equipmentForm.addControl('installedAt', new FormControl(this.equipment.installedAt || null));
    this.equipmentForm.addControl('purchasedAt', new FormControl(this.equipment.purchasedAt || null));

    if (this.isViewMode) {
      this.equipmentForm.disable();
    }

    this.equipmentForm.get('type').valueChanges.subscribe((equipmentType: EquipmentType) => {
      this.changeEquipmentType(equipmentType);
    });

    this.changeEquipmentType(this.equipmentForm.get('type').value);

    this.equipmentForm
      .get('location')
      .valueChanges
      .subscribe((value) => {
        this.equipmentForm.get('environment').setValue(null);
        this.equipmentForm.get('sector').setValue(null);
        this.equipmentForm.get('environment').disable();
        this.equipmentForm.get('sector')[value ? 'enable' : 'disable']();
      });

    this.equipmentForm.get('sector').valueChanges.subscribe((value) => {
      this.equipmentForm.get('environment').setValue(null);
      this.equipmentForm.get('environment')[value ? 'enable' : 'disable']();
    });

    this.matDialogRef.beforeClosed()
    .subscribe(() => {
      if (this.reloadDataTable) { window.emitEvent('equipment-closed'); }
      window.removeEventListener('equipment-warranties-updated', this.equipmentWarrantiesUpdatedHandler);
    });

    window.addEventListener('equipment-warranties-updated', this.equipmentWarrantiesUpdatedHandler);
  }

  ngAfterViewInit(): void {
    if (this.data.selectedTab) {
      this.selectedTabIndex = Number(DialogEquipmentTabsEnum[this.data.selectedTab]) || 0;
      this.changeDetectorRef.detectChanges();
    }
  }

  public changeEquipmentType(equipmentType: EquipmentType): void {
    if (equipmentType && equipmentType.id) {
      this.equipmentsTypesApi
        .getEquipmentTypeById(equipmentType.id)
        .subscribe((response) => {
          this.setCustomFields(response.customFields);
        });
    }
  }

  setCustomFields(customFields: CustomField[]): void {
    const customFieldsArray = this.formBuilder.array(customFields.map((customField) => {
      const customFieldFormGroup = this.formBuilder.group({
        name: this.formBuilder.control(customField.name),
        value: this.formBuilder.control(null),
        required: this.formBuilder.control(customField.required || false),
        type: this.formBuilder.control(customField.type),
        options: this.formBuilder.array(customField.options.map((option) => {
          return this.formBuilder.group({
            value: this.formBuilder.control(option.value),
          });
        }))
      });

      if (customField.required) {
        customFieldFormGroup.get('value').setValidators(Validators.required);
      }

      if (this.isViewMode) {
        customFieldFormGroup.disable();
      }

      return customFieldFormGroup;
    }));

    customFieldsArray.controls.forEach((customFieldFormGroup) => {
      const customFieldWithSameName = this.equipment.customFields.find(
        (customFieldFromEquipment) => customFieldFromEquipment.name === customFieldFormGroup.value.name
      );

      if (customFieldWithSameName) {
        customFieldFormGroup.get('value').setValue(customFieldWithSameName.value);
      }
    });

    this.equipmentForm.setControl('customFields', customFieldsArray);
    this.changeDetectorRef.detectChanges();
  }

  private equipmentWarrantiesUpdatedHandler = (): void => {
    this.reloadDataTable = true;
  }

  public async archiveOrUnarchiveEquipment(archived: boolean): Promise<void> {
    this.equipment.archived = archived;
    return this.generateInputToSave();
  }

  public async generateInputToSave(): Promise<void> {
    const formValue = this.equipmentForm.getRawValue();
    const equipmentInput = {
      archived: this.equipment.archived,
      name: formValue.name,
      notes: formValue.notes,
      number: formValue.number,
      qrCode: formValue.qrCode,
      equipmentTypeId: formValue.type.id,
      locationId: formValue.locationId,
      customFields: formValue.customFields.map((customField) => ({
        name: customField.name,
        value: String(customField.value),
        type: customField.type
      }))
    } as any;

    equipmentInput.locationId = formValue.location.id;
    equipmentInput.purchasedAt = formValue.purchasedAt;
    equipmentInput.installedAt = formValue.installedAt;
    equipmentInput.locationSectorId = formValue.sector ? formValue.sector.id : null;
    equipmentInput.locationEnvironmentId = formValue.environment ? formValue.environment.id : null;
    equipmentInput.imageUrl = formValue.imageUrl && formValue.imageUrl.thumb === this.equipment.imageUrl ? this.equipment.imageUrl : formValue.imageUrl;


    if (
      this.equipment &&
      this.equipment.id &&
      (equipmentInput.locationSectorId !== (this.equipment.locationSector && this.equipment.locationSector.id) ||
        equipmentInput.locationEnvironmentId !== (this.equipment.locationEnvironment && this.equipment.locationEnvironment.id))
    ) {
      const recurrences = await this.recurrenceService.listRecurrences({
        where: {
          equipmentId_in: this.equipment.id
        },
        options: {
          limit: 10000,
          offset: 0
        }
      });

      if (recurrences.items && recurrences.items.length) {
        const result = await this.openEquipmentRecurrencesOptions({ recurrences, equipmentInput });
        if (result !== 'confirm') { return; }
      }
    }


    equipmentInput.customFields.forEach((customField, index) => {
      if (!customField.value) {
        equipmentInput.customFields.splice(index, 1);
      }

      if (customField.type === 'date' && customField.value) {
        const date = new Date(customField.value);
        equipmentInput.customFields[index].value = isValid(date) ? date.toISOString() : null;
      }
    });

    return this.saveEquipment(equipmentInput);
  }

  async openEquipmentRecurrencesOptions({ recurrences, equipmentInput }): Promise<string> {
    return this.fcChoiceDialogService.open({
      title: this.translateService.instant('EQUIPMENTS.DIALOG.LINKED_AT_RECURRENCES_DIALOG.TITLE'),
      description: this.translateService.instant('EQUIPMENTS.DIALOG.LINKED_AT_RECURRENCES_DIALOG.DESCRIPTION'),
      options: [
        {
          text: this.translateService.instant('EQUIPMENTS.DIALOG.LINKED_AT_RECURRENCES_DIALOG.OPTIONS.KEEP_LINK'),
          action: async () => {
            await this.keepsEquipmentLinkedToRecurrences(recurrences, equipmentInput);
          }
        },
        {
          text: this.translateService.instant('EQUIPMENTS.DIALOG.LINKED_AT_RECURRENCES_DIALOG.OPTIONS.UNLINK'),
          action: async () => {
            await this.removeEquipmentLinkedToRecurrences(recurrences);
          }
        },
      ]
    });
  }

  public async saveEquipment(equipmentInput: Record<string, any>): Promise<void> {
    this.isSaving = true;
    this.progressBar.show();

    try {
      if (equipmentInput.imageUrl && equipmentInput.imageUrl.thumb && equipmentInput.imageUrl.thumb !== this.equipment.imageUrl) {
        const credential = await this.importsService.createUploadCredential({
          size: equipmentInput.imageUrl.size,
          extension: equipmentInput.imageUrl.filename.split('.').pop(),
          entity: UploadCredentialEntityEnum.equipments
        });

        await this.uploadService.uploadWithCredentials({
          fields: credential.fields,
          baseUrl: credential.baseUrl,
          file: equipmentInput.imageUrl.file
        });

        equipmentInput.imageUrl = credential.link;
      }

      const savedEquipment = await this.equipmentsService.saveEquipment(this.equipment.id, equipmentInput);

      this.customMessageService.success('EQUIPMENTS.SUCCESS_MESSAGE');
      this.matDialogRef.close({
        reload: true,
        equipment: savedEquipment
      });

      this.reloadDataTable = true;
    } catch (err) {
      const code = getErrorCode(err);

      if (code === 'equipment_qr_code_duplicated') {
        this.selectedTabIndex = 0;
        this.equipmentForm.get('qrCode').setErrors({
          'equipment_qr_code_duplicated': true
        });

        return;
      }

      if (code === 'equipment_number_duplicated') {
        this.selectedTabIndex = 0;
        this.equipmentForm.get('number').setErrors({
          'equipment_number_duplicated': true
        });

        return;
      }

      this.customMessageService.error('EQUIPMENTS.ERROR_MESSAGE');
      sentry.captureException(err);
    } finally {
      this.progressBar.hide();
      this.isSaving = false;
    }
  }

  gotoQrCodeTab(): void {
    this.selectedTabIndex = QR_CODE_TAB_INDEX;
  }

  async keepsEquipmentLinkedToRecurrences(recurrences, equipmentInput): Promise<void> {
    for (const recurrence of recurrences.items) {
      let newRecurrenceSectors = recurrence.locationSectors.map((sector) => sector.id);
      let newRecurrenceEnvironments = recurrence.locationEnvironments.map((environment) => environment.id);

      if (equipmentInput.locationSectorId) {
        newRecurrenceSectors = [...newRecurrenceSectors, equipmentInput.locationSectorId];
      }

      if (equipmentInput.locationEnvironmentId) {
        newRecurrenceEnvironments = [...newRecurrenceEnvironments, equipmentInput.locationEnvironmentId];
      }

      await this.recurrenceService.update(recurrence.id, {
        locationSectorIds: newRecurrenceSectors || undefined,
        locationEnvironmentIds: newRecurrenceEnvironments || undefined
      });
    }
  }

  async removeEquipmentLinkedToRecurrences(recurrences): Promise<void> {
    for (const recurrence of recurrences.items) {
      const newRecurrenceEquipments = recurrence.equipments.map((equipment) => equipment.id).filter((id) => id !== this.equipment.id);

      await this.recurrenceService.update(recurrence.id, {
        equipmentIds: newRecurrenceEquipments || undefined
      });
    }
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 1) {
      const container = document.getElementById('equipment-container');
      if (container) {
        container.innerHTML = '';

        setTimeout(() => {
          const element = document.createElement('equipment-with-warranties');
          (element as any).equipmentId = this.equipmentId;
          (element as any).hasCreateWarrantyPermission = this.hasCreateWarrantyPermission;
          (element as any).hasEditWarrantyPermission = this.hasEditWarrantyPermission;
          container.appendChild(element);
        }, 0);
      }
    }
  }

}
